import React, { useEffect, useState, useRef, useCallback } from "react";
import { Button, Table, Modal, Spinner } from "react-bootstrap";

import ProctoringAdminService from "../../_services/ProctoringAdminService";
import SummaryCards from "./components/SummaryCards";
import SessionDetailModal from "./components/SessionDetailModal";

interface Session {
  TestId: number;
  testName: string;
  EnquiryId: number;
  studentName: string;
  totalViolations: number;
  tabSwitches: number;
  fullscreenExits: number;
  focusLosses: number;
  rightClicks: number;
  devToolsAttempts: number;
  lastActivity: string;
  recordingChunks: number;
}

interface Summary {
  activeCount: number;
  flaggedCount: number;
  totalViolations: number;
}

const ProctoringMonitor: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [summary, setSummary] = useState<Summary>({
    activeCount: 0,
    flaggedCount: 0,
    totalViolations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);
  const [detailTestId, setDetailTestId] = useState<number | null>(null);
  const [detailEnquiryId, setDetailEnquiryId] = useState<number | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [blockTarget, setBlockTarget] = useState<Session | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [blocking, setBlocking] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchLive = useCallback(async () => {
    try {
      const res = await ProctoringAdminService.getLiveSessions();
      const d = res.data.data;
      setSessions(d.sessions || []);
      setSummary(d.summary || { activeCount: 0, flaggedCount: 0, totalViolations: 0 });
    } catch (err) {
      console.error("Failed to fetch live sessions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (polling) {
      intervalRef.current = setInterval(fetchLive, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [polling, fetchLive]);

  const handleViewDetail = (session: Session) => {
    setDetailTestId(session.TestId);
    setDetailEnquiryId(session.EnquiryId);
    setShowDetail(true);
  };

  const handleBlock = async () => {
    if (!blockTarget) return;
    setBlocking(true);
    try {
      await ProctoringAdminService.blockStudent({
        testId: blockTarget.TestId,
        enquiryId: blockTarget.EnquiryId,
        reason: blockReason || "Blocked during live monitoring",
      });
      setBlockTarget(null);
      setBlockReason("");
      fetchLive();
    } catch (err) {
      console.error("Failed to block student:", err);
    } finally {
      setBlocking(false);
    }
  };

  const getRowColor = (violations: number): string => {
    if (violations >= 4) return "#fef2f2";
    if (violations >= 1) return "#fffbeb";
    return "transparent";
  };

  const timeAgo = (dateStr: string): string => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    return `${mins}m ago`;
  };

  return (
    <div style={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h5 style={{ margin: 0 }}>Live Proctoring Monitor</h5>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {polling && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "var(--status-success)",
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "var(--status-success)",
                  display: "inline-block",
                }}
              />
              Live
            </span>
          )}
          <Button
            size="sm"
            variant={polling ? "secondary" : "primary"}
            onClick={() => setPolling(!polling)}
          >
            {polling ? "Pause" : "Resume"}
          </Button>
          <Button size="sm" variant="primary" onClick={fetchLive}>
            Refresh
          </Button>
        </div>
      </div>

      <SummaryCards
        activeCount={summary.activeCount}
        flaggedCount={summary.flaggedCount}
        totalViolations={summary.totalViolations}
      />

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spinner animation="border" size="sm" /> Loading live sessions...
        </div>
      ) : sessions.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            color: "var(--text-muted)",
            border: "1px solid var(--border-default)",
            borderRadius: 8,
          }}
        >
          No active proctored sessions right now.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table striped bordered hover size="sm" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Student</th>
                <th>Test</th>
                <th style={{ textAlign: "center" }}>Tab Sw.</th>
                <th style={{ textAlign: "center" }}>FS Exit</th>
                <th style={{ textAlign: "center" }}>Focus</th>
                <th style={{ textAlign: "center" }}>Total</th>
                <th style={{ textAlign: "center" }}>Recordings</th>
                <th>Last Active</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr
                  key={`${s.TestId}-${s.EnquiryId}`}
                  style={{ background: getRowColor(Number(s.totalViolations)) }}
                >
                  <td>{s.studentName}</td>
                  <td>{s.testName}</td>
                  <td style={{ textAlign: "center" }}>{s.tabSwitches}</td>
                  <td style={{ textAlign: "center" }}>{s.fullscreenExits}</td>
                  <td style={{ textAlign: "center" }}>{s.focusLosses}</td>
                  <td style={{ textAlign: "center", fontWeight: 600 }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "1px 8px",
                        borderRadius: 4,
                        background:
                          Number(s.totalViolations) >= 4
                            ? "#dc2626"
                            : Number(s.totalViolations) >= 1
                              ? "#d97706"
                              : "#16a34a",
                        color: "var(--text-on-brand)",
                        fontSize: 12,
                      }}
                    >
                      {s.totalViolations}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>{s.recordingChunks}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{timeAgo(s.lastActivity)}</td>
                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    <Button
                      size="sm"
                      variant="primary"
                      style={{ marginRight: 4, fontSize: 11, padding: "2px 8px" }}
                      onClick={() => handleViewDetail(s)}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      style={{ fontSize: 11, padding: "2px 8px" }}
                      onClick={() => setBlockTarget(s)}
                    >
                      Block
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Block Confirmation Modal */}
      <Modal show={!!blockTarget} onHide={() => setBlockTarget(null)} centered>
        <Modal.Header>
          <Modal.Title style={{ fontSize: "1rem" }}>Block Student</Modal.Title>
          <button
            type="button"
            onClick={() => setBlockTarget(null)}
            aria-label="Close"
            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer" }}
          >
            &times;
          </button>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14 }}>
            Block <strong>{blockTarget?.studentName}</strong> from{" "}
            <strong>{blockTarget?.testName}</strong>?
          </p>
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            This will immediately end their exam session. This action cannot be undone.
          </p>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Reason for blocking (optional)"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={() => setBlockTarget(null)}>
            Cancel
          </Button>
          <Button size="sm" variant="danger" onClick={handleBlock} disabled={blocking}>
            {blocking ? "Blocking..." : "Confirm Block"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Session Detail Modal */}
      <SessionDetailModal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        testId={detailTestId}
        enquiryId={detailEnquiryId}
        onReviewSubmitted={fetchLive}
        onStudentBlocked={fetchLive}
      />
    </div>
  );
};

export default ProctoringMonitor;
